import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  AgentPublishDecide,
  AgentPublishRequest,
  AgentPublishRequestCreate,
} from "@/api/types";

const KEY = (agentId: string) =>
  ["agent-publish-requests", agentId] as const;

export function useAgentPublishRequests(agentId: string) {
  return useQuery({
    queryKey: KEY(agentId),
    queryFn: () =>
      api.GET<AgentPublishRequest[]>(`/agents/${agentId}/publish-requests`),
    enabled: Boolean(agentId),
  });
}

export function useRequestPublish(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AgentPublishRequestCreate) =>
      api.POST<AgentPublishRequest>(
        `/agents/${agentId}/publish-requests`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY(agentId) });
      void qc.invalidateQueries({ queryKey: ["agent", agentId] });
    },
  });
}

export function useDecidePublish(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      requestId: string;
      body: AgentPublishDecide;
    }) =>
      api.POST<AgentPublishRequest>(
        `/agents/${agentId}/publish-requests/${params.requestId}/decide`,
        params.body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY(agentId) });
      void qc.invalidateQueries({ queryKey: ["agent", agentId] });
    },
  });
}

export function useUnpublishAgent(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.POST<import("@/api/types").AgentDetail>(
        `/agents/${agentId}/unpublish`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["agent", agentId] });
      void qc.invalidateQueries({ queryKey: KEY(agentId) });
    },
  });
}
